/**
 * <ry-select>
 *
 * Custom select dropdown component.
 *
 * Usage:
 * <ry-select placeholder="Choose country" name="country">
 *   <ry-option value="us">United States</ry-option>
 *   <ry-option value="uk">United Kingdom</ry-option>
 *   <ry-option value="ca" disabled>Canada</ry-option>
 * </ry-select>
 *
 * JS uses data-ry-target for queries, CSS uses classes for styling.
 */

import { RyElement } from '../core/ry-element.js';
import type { SelectOption, ChangeDetail } from '../types.js';

let selectIdCounter = 0;

export class RySelect extends RyElement {
  #selectId: string | null = null;
  #highlightedIndex = -1;
  #typeahead = '';
  #typeaheadTimeout: ReturnType<typeof setTimeout> | null = null;

  // Store options reference
  _options: SelectOption[] = [];

  static observedAttributes = ['value', 'disabled'] as const;

  setup(): void {
    this.#selectId = `ry-select-${++selectIdCounter}`;
    this.#transformMarkup();

    // Make focusable
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    // Events
    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);
    this.on(document, 'click', this.#handleOutsideClick);

    // Initialize state
    if (!this.hasAttribute('data-ry-state')) {
      this.state = 'closed';
    }

    // Set initial value
    const initialValue = this.getAttribute('value');
    if (initialValue) {
      this.value = initialValue;
    }
  }

  #transformMarkup(): void {
    // Collect options from ry-option children
    const options: SelectOption[] = [...this.querySelectorAll('ry-option')].map(opt => ({
      value: opt.getAttribute('value') ?? opt.textContent?.trim() ?? '',
      label: opt.textContent?.trim() ?? '',
      disabled: opt.hasAttribute('disabled')
    }));

    const placeholder = this.getAttribute('placeholder') ?? 'Select...';
    const name = this.getAttribute('name') ?? '';
    const disabled = this.hasAttribute('disabled');

    // Build native select for form submission
    const nativeOptions = options.map(opt =>
      `<option value="${opt.value}"${opt.disabled ? ' disabled' : ''}>${opt.label}</option>`
    ).join('');

    // Build custom dropdown
    // data-ry-target for JS queries, class for CSS styling
    const listboxOptions = options.map((opt, i) => `
      <div data-ry-target="option" class="ry-select__option"
           role="option"
           id="${this.#selectId}-option-${i}"
           data-value="${opt.value}"
           ${opt.disabled ? 'data-disabled' : ''}
           aria-disabled="${opt.disabled}">
        ${opt.label}
      </div>
    `).join('');

    this.innerHTML = `
      <div data-ry-target="trigger" class="ry-select__trigger" aria-haspopup="listbox" aria-expanded="false">
        <span data-ry-target="value" class="ry-select__value">${placeholder}</span>
        <span data-ry-target="arrow" class="ry-select__arrow">▾</span>
      </div>
      <div data-ry-target="dropdown" class="ry-select__dropdown" role="listbox" id="${this.#selectId}-listbox" tabindex="-1">
        ${listboxOptions}
      </div>
      <select data-ry-target="native" class="ry-select__native" ${name ? `name="${name}"` : ''} tabindex="-1" aria-hidden="true" ${disabled ? 'disabled' : ''}>
        <option value="">${placeholder}</option>
        ${nativeOptions}
      </select>
    `;

    // Store options reference
    this._options = options;
  }

  #handleClick = (event: MouseEvent): void => {
    if (this.hasAttribute('disabled')) return;

    const target = event.target as Element;
    const option = target.closest('[data-ry-target="option"]');
    if (option instanceof HTMLElement && !option.hasAttribute('data-disabled')) {
      this.#selectOption(option);
      this.close();
      return;
    }

    const trigger = target.closest('[data-ry-target="trigger"]');
    if (trigger) {
      this.toggle();
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (this.hasAttribute('disabled')) return;

    const isOpen = this.state === 'open';

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (isOpen && this.#highlightedIndex >= 0) {
          const options = this.$$<HTMLElement>('[data-ry-target="option"]:not([data-disabled])');
          const option = options[this.#highlightedIndex];
          if (option) {
            this.#selectOption(option);
            this.close();
          }
        } else {
          this.toggle();
        }
        break;

      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          this.close();
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          this.open();
        } else {
          this.#highlightNext();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          this.#highlightPrev();
        }
        break;

      case 'Home':
        if (isOpen) {
          event.preventDefault();
          this.#highlightFirst();
        }
        break;

      case 'End':
        if (isOpen) {
          event.preventDefault();
          this.#highlightLast();
        }
        break;

      default:
        // Typeahead
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
          this.#handleTypeahead(event.key);
        }
        break;
    }
  };

  #handleOutsideClick = (event: MouseEvent): void => {
    const target = event.target as Node;
    if (!this.contains(target) && this.state === 'open') {
      this.close();
    }
  };

  #handleTypeahead(char: string): void {
    // Clear timeout
    if (this.#typeaheadTimeout) {
      clearTimeout(this.#typeaheadTimeout);
    }

    this.#typeahead += char.toLowerCase();

    // Find matching option
    const options = this.$$<HTMLElement>('[data-ry-target="option"]:not([data-disabled])');
    const match = options.findIndex(opt =>
      opt.textContent?.trim().toLowerCase().startsWith(this.#typeahead)
    );

    if (match >= 0) {
      if (this.state === 'open') {
        this.#highlightIndex(match);
      } else {
        const option = options[match];
        if (option) {
          this.#selectOption(option);
        }
      }
    }

    // Clear typeahead after 500ms
    this.#typeaheadTimeout = setTimeout(() => {
      this.#typeahead = '';
    }, 500);
  }

  #highlightIndex(index: number): void {
    const options = this.$$<HTMLElement>('[data-ry-target="option"]:not([data-disabled])');
    if (index < 0 || index >= options.length) return;

    // Remove previous highlight
    this.$$('[data-ry-target="option"][data-highlighted]').forEach(opt => {
      opt.removeAttribute('data-highlighted');
    });

    // Add new highlight
    const option = options[index];
    if (!option) return;

    option.setAttribute('data-highlighted', '');
    option.scrollIntoView({ block: 'nearest' });
    this.#highlightedIndex = index;

    // Update aria-activedescendant
    const trigger = this.$('[data-ry-target="trigger"]');
    if (trigger) {
      trigger.setAttribute('aria-activedescendant', option.id);
    }
  }

  #highlightNext(): void {
    const options = this.$$('[data-ry-target="option"]:not([data-disabled])');
    const next = Math.min(this.#highlightedIndex + 1, options.length - 1);
    this.#highlightIndex(next);
  }

  #highlightPrev(): void {
    const prev = Math.max(this.#highlightedIndex - 1, 0);
    this.#highlightIndex(prev);
  }

  #highlightFirst(): void {
    this.#highlightIndex(0);
  }

  #highlightLast(): void {
    const options = this.$$('[data-ry-target="option"]:not([data-disabled])');
    this.#highlightIndex(options.length - 1);
  }

  #selectOption(optionEl: HTMLElement): void {
    const value = optionEl.dataset.value ?? '';
    const label = optionEl.textContent?.trim() ?? '';

    // Update display
    const valueEl = this.$('[data-ry-target="value"]');
    if (valueEl) {
      valueEl.textContent = label;
      valueEl.setAttribute('data-selected', '');
    }

    // Update native select
    const nativeSelect = this.$<HTMLSelectElement>('[data-ry-target="native"]');
    if (nativeSelect) {
      nativeSelect.value = value;
    }

    // Update attribute
    this.setAttribute('value', value);

    // Update selected state
    this.$$('[data-ry-target="option"]').forEach(opt => {
      opt.removeAttribute('aria-selected');
    });
    optionEl.setAttribute('aria-selected', 'true');

    this.emit<ChangeDetail>('change', { value, label });
  }

  open(): void {
    if (this.state === 'open') return;

    this.state = 'open';

    const trigger = this.$('[data-ry-target="trigger"]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    // Smart positioning: flip to top if would go off screen
    this.#positionDropdown();

    // Highlight current value or first option
    const currentValue = this.getAttribute('value');
    if (currentValue) {
      const options = this.$$<HTMLElement>('[data-ry-target="option"]:not([data-disabled])');
      const index = options.findIndex(opt => opt.dataset.value === currentValue);
      if (index >= 0) {
        this.#highlightIndex(index);
      }
    } else {
      this.#highlightIndex(0);
    }

    this.emit('open');
  }

  #positionDropdown(): void {
    const dropdown = this.$('[data-ry-target="dropdown"]');
    if (!dropdown) return;

    // Reset position
    this.removeAttribute('data-ry-position');

    // Check if dropdown would go off bottom of screen
    const rect = this.getBoundingClientRect();
    const dropdownHeight = (dropdown as HTMLElement).scrollHeight || 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // If not enough space below but more space above, flip to top
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      this.setAttribute('data-ry-position', 'top');
    }
  }

  close(): void {
    if (this.state === 'closed') return;

    this.state = 'closed';

    const trigger = this.$('[data-ry-target="trigger"]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    // Clear highlight
    this.$$('[data-ry-target="option"][data-highlighted]').forEach(opt => {
      opt.removeAttribute('data-highlighted');
    });
    this.#highlightedIndex = -1;

    this.emit('close');
  }

  toggle(): void {
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  }

  get value(): string {
    return this.getAttribute('value') ?? '';
  }

  set value(val: string) {
    const option = this.$<HTMLElement>(`[data-ry-target="option"][data-value="${val}"]`);
    if (option) {
      this.#selectOption(option);
    }
  }

  teardown(): void {
    if (this.#typeaheadTimeout) {
      clearTimeout(this.#typeaheadTimeout);
    }
  }
}

// Register placeholder element
class RyOption extends HTMLElement {}
customElements.define('ry-option', RyOption);

customElements.define('ry-select', RySelect);
